import type { RefObject } from "react";
import { useEffect, useLayoutEffect } from "react";

interface ScrollRestorationConfig {
  scrollRef: RefObject<HTMLElement>;
  childRef: RefObject<HTMLElement>;
  pathname: string;
  behavior?: ScrollBehavior;
}

type DebouncedFn = ((...args: unknown[]) => unknown) & { _tId?: ReturnType<typeof setTimeout> };

const useScrollRestoration = ({
  scrollRef,
  childRef,
  pathname,
  behavior = "instant",
}: ScrollRestorationConfig) => {
  if (behavior !== "instant" && behavior !== "smooth") {
    throw new Error(`behavior needs to be 'instant' or 'smooth' and it is currently ${behavior}`);
  }

  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) {
      return;
    }

    const handleScroll = () => {
      if (!childRef.current) {
        return;
      }
      const position = childRef.current.getBoundingClientRect().top - 64;
      updateScrollPosition(pathname, position);
    };

    const onScroll = () => debounce(handleScroll as DebouncedFn, 100);

    scrollElement.addEventListener("scroll", onScroll);

    return () => {
      scrollElement.removeEventListener("scroll", onScroll);
    };
  }, [scrollRef, childRef, pathname]);

  useLayoutEffect(() => {
    const scrollPosition = Math.abs(getScrollPosition(location.pathname) || 0);
    scrollRef.current?.scrollTo({
      top: Math.abs(scrollPosition),
      left: 0,
      behavior,
    });
  }, [scrollRef, behavior]);
};

function getPositionMap(): Record<string, number> {
  const positionMap: Record<string, number> = {};
  const stored = localStorage.getItem("scrollPositionMap");

  if (stored) {
    try {
      Object.assign(positionMap, JSON.parse(stored));
    } catch (_) {
      /* empty */
    }
  }

  return positionMap;
}

function updateScrollPosition(key: string, position: number): void {
  const positionMap = getPositionMap();
  positionMap[key] = position;
  localStorage.setItem("scrollPositionMap", JSON.stringify(positionMap));
}

function getScrollPosition(key: string): number | null {
  // Disable except homepage
  if (location.pathname.length > 1) {
    return 0;
  }

  const positionMap = getPositionMap();

  if (Object.hasOwn(positionMap, key) && positionMap[key]) {
    return positionMap[key];
  }

  return null;
}

function debounce(method: DebouncedFn, delay: number): void {
  clearTimeout(method._tId);
  method._tId = setTimeout(() => {
    method();
  }, delay);
}

export default useScrollRestoration;
